import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrench, Languages, Landmark, Microscope, ChevronDown, Video, Paperclip, BookOpenCheck, Loader2, Lock } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { GospelLensIcon } from '@/components/GospelLensIcon';
import { toast } from 'sonner';

const STUDY_TOOLS = [
  {
    key: 'deep_study_enabled',
    icon: Microscope,
    labelKey: 'tools.deep_study',
    descKey: 'tools.deep_study_desc',
    color: '#10b981',
    premiumOnly: true,
  },
  {
    key: 'historical_context_enabled',
    icon: Landmark,
    labelKey: 'tools.historical_context',
    descKey: 'tools.historical_context_desc',
    color: '#0891b2',
    premiumOnly: true,
  },
  {
    key: 'greek_analysis_enabled',
    icon: Languages,
    labelKey: 'tools.greek_analysis',
    descKey: 'tools.greek_analysis_desc',
    color: '#6366f1',
    premiumOnly: true,
  },
  {
    key: 'gospel_lens_enabled',
    icon: GospelLensIcon,
    labelKey: 'tools.gospel_lens',
    descKey: 'tools.gospel_lens_desc',
    color: '#f59e0b',
    premiumOnly: true,
  },
  {
    key: 'simple_language',
    icon: BookOpenCheck,
    labelKey: 'tools.easy_read',
    descKey: 'tools.easy_read_desc',
    color: '#ec4899',
    premiumOnly: false,
  },
];

export default function StudyToolsPopover({
  userSettings,
  onToggle,
  isPaid,
  onShowUpgrade,
  fileInputRef,
  fileUploading,
  youtubeUrl,
  setYoutubeUrl,
  youtubeAnalyzing,
  onAnalyzeYouTube,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showYoutubeInput, setShowYoutubeInput] = useState(false);
  const triggerRef = React.useRef(null);

  // Dynamic upper-bound on the popover height. Radix's `collisionPadding`
  // only constrains a popover that's about to overflow the *viewport
  // edge* — it does NOT understand arbitrary in-page chrome like the
  // sticky Eili <header>. So even though the popover opens upward
  // toward the top of the screen, Radix happily places its top at
  // trigger.top - height regardless of any header in the way.
  //
  // The definitive fix is to compute the actual space available
  // between the header's bottom and the trigger's top, then cap
  // `maxHeight` to that. Anything that doesn't fit becomes scrollable
  // inside the inner container.
  const [maxHeightPx, setMaxHeightPx] = useState(null);
  useEffect(() => {
    if (!open) return;
    const SIDE_OFFSET = 8;
    const GAP_BELOW_HEADER = 8;
    const measure = () => {
      try {
        const header = document.querySelector('header');
        const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const trigger = triggerRef.current;
        const triggerTop = trigger ? trigger.getBoundingClientRect().top : window.innerHeight;
        // Available room above the trigger, below the header.
        const available = triggerTop - headerBottom - SIDE_OFFSET - GAP_BELOW_HEADER;
        // Clamp to a reasonable minimum so the popover stays usable
        // even in extreme keyboard-up scenarios.
        const capped = Math.max(180, Math.min(window.innerHeight - 40, available));
        setMaxHeightPx(capped);
      } catch {}
    };
    measure();
    window.addEventListener('resize', measure);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', measure);
    };
  }, [open]);

  const activeCount = STUDY_TOOLS.filter(tool => {
    if (tool.premiumOnly && !isPaid) return false;
    return !!userSettings?.[tool.key];
  }).length;

  const handleYoutubeSubmit = () => {
    if (!youtubeUrl) return;
    toast.info(t('tools.analyzing_toast'), { duration: 4000 });
    onAnalyzeYouTube();
    setShowYoutubeInput(false);
    setOpen(false);
  };

  const handleFileClick = () => {
    fileInputRef?.current?.click();
    setOpen(false);
    toast.info(t('tools.select_file_toast'), { duration: 3000 });
  };

  const handleUpgrade = (feature) => {
    setOpen(false);
    onShowUpgrade(feature);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setShowYoutubeInput(false); }}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-medium transition-all hover:scale-[1.02] active:scale-95"
          style={{
            background: activeCount > 0 ? 'var(--theme-accentLight)' : 'var(--glass-bg)',
            border: `1px solid ${activeCount > 0 ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
            color: activeCount > 0 ? 'var(--theme-accent)' : 'var(--theme-textSecondary)',
            backdropFilter: 'blur(8px)',
          }}
          data-testid="study-tools-trigger"
        >
          <Wrench className="h-3.5 w-3.5" />
          {t('tools.title')}
          {activeCount > 0 && (
            <span
              className="flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
            >
              {activeCount}
            </span>
          )}
          <ChevronDown
            className="h-3 w-3 transition-transform duration-200"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={8}
        // collisionPadding is kept generous for the iOS notch but the
        // *real* upper bound is enforced via `maxHeight` below — that's
        // because Radix's collisionPadding only fires when the popover
        // would overflow the viewport edge, NOT when it overlaps with
        // an in-page sticky header. We compute the actual available
        // space between the trigger's top and the header's bottom and
        // cap maxHeight to it. Overflow → scroll inside.
        collisionPadding={{ top: 80, bottom: 12, left: 8, right: 8 }}
        className="w-80 p-0 rounded-2xl border shadow-xl overflow-hidden flex flex-col z-[70]"
        style={{
          background: 'var(--theme-surface)',
          borderColor: 'var(--theme-border)',
          backdropFilter: 'blur(20px)',
          // Hard cap: never grow past the room between the trigger
          // top and the sticky <header> bottom. `maxHeightPx` is
          // measured on open and re-measured on resize / keyboard show.
          // Fallback to 70vh until the first measurement lands.
          maxHeight: maxHeightPx
            ? `${maxHeightPx}px`
            : 'min(70vh, calc(var(--visual-vh, 100vh) - 160px))',
        }}
      >
        {/* Inner wrapper is the scroll container so the rounded
            border + backdrop blur on PopoverContent stay crisp. */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="study-tools-scroll">
        {/* Study Tools Section */}
        <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--theme-border)' }}>
          <p className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('tools.study_section')}
          </p>
        </div>
        <div className="py-1">
          {STUDY_TOOLS.map((tool) => {
            const Icon = tool.icon;
            const locked = tool.premiumOnly && !isPaid;
            const enabled = !locked && !!userSettings?.[tool.key];
            return (
              <div
                key={tool.key}
                className="flex items-center justify-between px-4 py-2 transition-colors"
                style={locked ? { cursor: 'pointer' } : undefined}
                onClick={locked ? () => handleUpgrade(tool.key) : undefined}
                data-testid={`study-tool-${tool.key}`}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-3" style={locked ? { opacity: 0.4 } : undefined}>
                  <div
                    className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                    style={{ background: enabled ? `${tool.color}18` : 'var(--glass-bg)' }}
                  >
                    <Icon
                      className="h-3.5 w-3.5"
                      style={{ color: enabled ? tool.color : 'var(--theme-textSecondary)' }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p
                      className="text-sm font-medium leading-tight"
                      style={{ color: enabled ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}
                    >
                      {t(tool.labelKey)}
                    </p>
                    <p className="text-[10px] leading-snug mt-0.5" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }}>
                      {t(tool.descKey)}
                    </p>
                  </div>
                </div>
                {locked ? (
                  <Lock className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--theme-textSecondary)', opacity: 0.35 }} />
                ) : (
                  <Switch
                    checked={enabled}
                    onCheckedChange={(checked) => onToggle(tool.key, checked)}
                    data-testid={`study-tool-toggle-${tool.key}`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Import & Analyze Section */}
        <div className="px-4 py-2.5 border-t border-b" style={{ borderColor: 'var(--theme-border)' }}>
          <p className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('tools.import_section')}
          </p>
        </div>
        <div className="py-1">
          {/* File Upload */}
          <button
            onClick={isPaid ? handleFileClick : () => handleUpgrade('file_upload')}
            disabled={isPaid && fileUploading}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 transition-colors hover:bg-[var(--theme-hover)] text-left"
            data-testid="tool-file-upload"
          >
            <div
              className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
              style={{ background: 'rgba(168,85,247,0.1)', opacity: isPaid ? 1 : 0.4 }}
            >
              {isPaid && fileUploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: '#a855f7' }} />
              ) : (
                <Paperclip className="h-3.5 w-3.5" style={{ color: '#a855f7' }} />
              )}
            </div>
            <div className="min-w-0" style={isPaid ? undefined : { opacity: 0.4 }}>
              <p className="text-sm font-medium leading-tight" style={{ color: 'var(--theme-text)' }}>
                {isPaid && fileUploading ? t('tools.uploading') : t('tools.upload_file')}
              </p>
              <p className="text-[10px] leading-tight mt-0.5" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }}>
                {isPaid && fileUploading ? t('tools.uploading_desc') : t('tools.upload_file_desc')}
              </p>
            </div>
            {!isPaid && <Lock className="h-3.5 w-3.5 flex-shrink-0 ml-auto" style={{ color: 'var(--theme-textSecondary)', opacity: 0.35 }} />}
          </button>

          {/* YouTube Analysis */}
          <div data-testid="tool-youtube-analysis">
            <button
              onClick={isPaid ? () => setShowYoutubeInput(!showYoutubeInput) : () => handleUpgrade('youtube')}
              disabled={isPaid && youtubeAnalyzing}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 transition-colors hover:bg-[var(--theme-hover)] text-left"
            >
              <div
                className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                style={{ background: 'rgba(239,68,68,0.1)', opacity: isPaid ? 1 : 0.4 }}
              >
                {isPaid && youtubeAnalyzing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: '#ef4444' }} />
                ) : (
                  <Video className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />
                )}
              </div>
              <div className="min-w-0" style={isPaid ? undefined : { opacity: 0.4 }}>
                <p className="text-sm font-medium leading-tight" style={{ color: 'var(--theme-text)' }}>
                  {isPaid && youtubeAnalyzing ? t('tools.analyzing') : t('tools.youtube')}
                </p>
                <p className="text-[10px] leading-tight mt-0.5" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }}>
                  {isPaid && youtubeAnalyzing ? t('tools.analyzing_desc') : t('tools.youtube_desc')}
                </p>
              </div>
              {!isPaid && <Lock className="h-3.5 w-3.5 flex-shrink-0 ml-auto" style={{ color: 'var(--theme-textSecondary)', opacity: 0.35 }} />}
            </button>
            {isPaid && showYoutubeInput && !youtubeAnalyzing && (
              <div className="px-4 pb-3 flex gap-2">
                <Input
                  placeholder={t('tools.paste_url')}
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  data-testid="youtube-url-input"
                  className="h-8 text-xs rounded-lg flex-1"
                  style={{ background: 'var(--glass-bg)', borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleYoutubeSubmit(); }}
                />
                <Button
                  size="sm"
                  onClick={handleYoutubeSubmit}
                  disabled={youtubeAnalyzing || !youtubeUrl}
                  className="h-8 px-3 rounded-lg text-xs"
                  data-testid="analyze-youtube-button"
                >
                  Go
                </Button>
              </div>
            )}
          </div>
        </div>
        </div>
        {/* /scroll container */}
      </PopoverContent>
    </Popover>
  );
}
